import {
  contrastRatio,
  hexToRgb,
  invertColor,
  RGB_FORMAT_OUTPUT,
  relativeLuminance,
  rgbToHex,
  rgbToHsl,
} from "@/features/utils/color";
import type { CustomColorTheme } from "./types";

const hexToRgbArray = (color: string) => {
  return hexToRgb(color, RGB_FORMAT_OUTPUT.ARRAY) as number[];
};

export const toCustomColorTheme = (theme: {
  brandColor: string;
  primaryColor: string;
  secondaryColor: string;
  warningColor: string;
  primaryFontColor: string;
  secondaryFontColor: string | null;
  iconColor: string;
  backgroundColor: string;
  hoverColor?: string;
}): CustomColorTheme => {
  const primaryColor = hexToRgbArray(theme.primaryColor);

  return {
    background: hexToRgbArray(theme.backgroundColor),
    brand: hexToRgbArray(theme.brandColor),
    fontPrimary: hexToRgbArray(theme.primaryFontColor),
    fontSecondary: theme.secondaryFontColor
      ? hexToRgbArray(theme.secondaryFontColor)
      : invertColor(primaryColor, true),
    hover: theme.hoverColor ? hexToRgbArray(theme.hoverColor) : undefined,
    iconColor: hexToRgbArray(theme.iconColor),
    primary: primaryColor,
    secondary: hexToRgbArray(theme.secondaryColor),
    warning: hexToRgbArray(theme.warningColor),
  };
};

const formatRgbCss = (rgb: number[] | undefined) =>
  rgb ? rgb.slice(0, 3).join(",") : "";

const formatHexCss = (rgb: number[] | undefined) =>
  rgb ? rgbToHex(rgb[0], rgb[1], rgb[2]) : "";

export const setCSSColorVariables = ({
  theme,
  suffix,
}: {
  theme: CustomColorTheme;
  suffix?: "-dark";
}) => {
  const s = suffix ?? "";
  const root = document.documentElement.style;

  root.setProperty(`--ui-primary-color${s}`, formatRgbCss(theme.primary));
  root.setProperty(`--ui-secondary-color${s}`, formatRgbCss(theme.secondary));
  root.setProperty("--ui-warning-color", formatRgbCss(theme.warning));
  root.setProperty(
    `--ui-primary-font-color${s}`,
    formatRgbCss(theme.fontPrimary),
  );
  root.setProperty(
    `--ui-secondary-font-color${s}`,
    formatRgbCss(theme.fontSecondary),
  );
  root.setProperty(`--ui-icon-color${s}`, formatRgbCss(theme.iconColor));
  root.setProperty(`--ui-background-color${s}`, formatHexCss(theme.background));
  root.setProperty(`--ui-hover-color${s}`, formatRgbCss(theme.hover));
  root.setProperty("--primary-color", formatHexCss(theme.primary));
  root.setProperty("--warning-color", formatHexCss(theme.warning));

  const brandHsl =
    theme.brand && theme.brand.length >= 3
      ? rgbToHsl(theme.brand[0], theme.brand[1], theme.brand[2])
      : undefined;

  // Kiosk mode
  root.setProperty("--k-color-brand-hue", brandHsl ? brandHsl.hue : "");
  root.setProperty(
    "--k-color-brand-saturation",
    brandHsl ? `${brandHsl.saturation}%` : "",
  );
  root.setProperty(
    "--k-color-brand-lightness",
    brandHsl ? `${brandHsl.lightness}%` : "",
  );

  if (theme.brand && theme.brand.length >= 3) {
    const [r, g, b] = theme.brand;
    const brandLum = relativeLuminance(r, g, b);
    const whiteRatio = contrastRatio(brandLum, 1.0);
    const blackRatio = contrastRatio(brandLum, 0.0);
    const needsLightText = whiteRatio >= 4.5 && whiteRatio >= blackRatio;

    const dataColorTheme =
      document.querySelector("html")?.getAttribute("data-color-theme") ?? "";

    const usePrimary = dataColorTheme === "" ? !needsLightText : needsLightText;

    root.setProperty(
      "--k-color-content-on-brand",
      `var(--k-color-content-${usePrimary ? "primary" : "primary-inverse"})`,
    );

    root.setProperty(
      "--k-color-icon-on-brand",
      `var(--k-color-icon-${usePrimary ? "primary" : "primary-inverse"})`,
    );
  }
};
